import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, Bell, PlayCircle,
  LineChart, Flame, Zap, Droplet, Layers,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { useSite } from '../context/SiteContext';
import { useAlert } from '../context/AlertContext';
import KpiCard from '../components/oim/KpiCard';
import IntensityChart from '../components/oim/IntensityChart';
import StatusIndicator from '../components/oim/StatusIndicator';
import OimUpload from '../components/oim/OimUpload';
import Heatmap from '../components/oim/Heatmap';
import DailySummaryPanel from '../components/oim/DailySummaryPanel';
import OimExecutivePanel from '../components/oim/OimExecutivePanel';
import OimSteamPanel from '../components/oim/OimSteamPanel';
import OimElectricityPanel from '../components/oim/OimElectricityPanel';
import OimWaterPanel from '../components/oim/OimWaterPanel';
import OimSEUAnalyticsPanel from '../components/oim/OimSEUAnalyticsPanel';

const SUB_TABS = [
  { id: 'operational', label: 'Operational',  num: 'M0', Icon: Activity },
  { id: 'executive',   label: 'Executive',    num: 'M1', Icon: LineChart },
  { id: 'steam',       label: 'Steam',        num: 'M2', Icon: Flame },
  { id: 'electricity', label: 'Electricity',  num: 'M3', Icon: Zap },
  { id: 'water',       label: 'Water',        num: 'M4', Icon: Droplet },
  { id: 'seu',         label: 'SEU Analytics', num: 'M5', Icon: Layers },
];

/**
 * OIM (Online Intelligence Module) Hub — Vertical slice MVP.
 *
 * Layout (§7.1, simplified for MVP):
 *   - Header: title, status indicator, period selector, refresh
 *   - KPI cards: Energy Intensity avg, Cost Intensity avg, Production total, Energy total
 *   - Trend chart: hourly Energy Intensity
 *   - Alerts panel: recent OIM alerts for the site
 *   - Ingestion section: 3 CSV uploaders (energy, production, cost)
 *
 * Data fetched from /api/oim/dashboard?site_id=&period=24h.
 * Alerts fetched from /api/oim/alerts?site_id=.
 */

const PERIOD_OPTIONS = [
  { id: '6h',  label: 'Last 6h',       hours: 6 },
  { id: '24h', label: 'Last 24h',      hours: 24 },
  { id: '7d',  label: 'Last 7 days',   hours: 24 * 7 },
  { id: '30d', label: 'Last 30 days',  hours: 24 * 30 },
];

// Compact "Apr 04, 12:30 PM → May 04, 12:30 PM" instead of toLocaleString's verbose default.
const formatRangeShort = (startIso, endIso) => {
  if (!startIso || !endIso) return '';
  const opts = { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' };
  try {
    const s = new Date(startIso).toLocaleString(undefined, opts);
    const e = new Date(endIso).toLocaleString(undefined, opts);
    return `${s} → ${e}`;
  } catch {
    return '';
  }
};

const OnlineIntelligenceHub = ({ user, onLogout }) => {
  const { activeSiteId } = useSite();
  const { showSuccess, showError } = useAlert();

  const [activeView, setActiveView] = useState('operational');
  const [period, setPeriod] = useState('24h');
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertPage, setAlertPage] = useState(1);
  const ALERT_PAGE_SIZE = 5;
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  // ── BEGIN DEMO ── (temporary; see docs/DEMO_DATA_FEATURE.md to remove) ──
  // Demo / test data generator state — poll every 5s while running so the
  // button text and tick counter stay live.
  const [demoStatus, setDemoStatus] = useState({ running: false });
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoBackfillHours, setDemoBackfillHours] = useState(24);
  // ── END DEMO ──

  const fetchDashboard = useCallback(async () => {
    if (!activeSiteId) {
      setDashboard(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: activeSiteId, period });
      const response = await axios.get(`${API}/oim/dashboard?${params.toString()}`);
      setDashboard(response.data);
    } catch (err) {
      console.error('OIM dashboard fetch failed', err);
      const msg = (err.response && err.response.data && err.response.data.detail) || 'Failed to load OIM dashboard';
      showError(msg);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [activeSiteId, period, showError]);

  const fetchAlerts = useCallback(async () => {
    if (!activeSiteId) {
      setAlerts([]);
      return;
    }
    try {
      const params = new URLSearchParams({ site_id: activeSiteId, unack_only: 'false', limit: '50' });
      const response = await axios.get(`${API}/oim/alerts?${params.toString()}`);
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('OIM alerts fetch failed', err);
      setAlerts([]);
    }
  }, [activeSiteId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  // Snap pagination back to page 1 whenever the alert list churns (site change, refresh)
  useEffect(() => { setAlertPage(1); }, [alerts.length, activeSiteId]);

  const handleCompute = async () => {
    if (!activeSiteId) {
      showError('Select a site first');
      return;
    }
    setComputing(true);
    try {
      // Cover 2× the active period so the dashboard's previous-window trend
      // calculation has intensity rows to compare against. Without this, trend
      // is null on any period whose previous window hasn't been computed yet
      // (e.g. switching to 24h after only the last 24h was materialized).
      const periodHours = PERIOD_OPTIONS.find(p => p.id === period)?.hours || 24;
      const end = new Date();
      end.setUTCMinutes(0, 0, 0);
      end.setUTCHours(end.getUTCHours() + 1); // top of next hour
      const start = new Date(end.getTime() - periodHours * 2 * 3600 * 1000);

      const response = await axios.post(`${API}/oim/compute`, {
        site_id: activeSiteId,
        range_start: start.toISOString(),
        range_end: end.toISOString(),
      });
      const data = response.data;
      showSuccess(
        `Computed ${data.compute.rows_computed} hourly rows · ${data.alerts.alerts_inserted} new alert(s)`
      );
      await Promise.all([fetchDashboard(), fetchAlerts()]);
    } catch (err) {
      console.error('OIM compute failed', err);
      const msg = (err.response && err.response.data && err.response.data.detail) || 'Compute failed';
      showError(msg);
    } finally {
      setComputing(false);
    }
  };

  // ── BEGIN DEMO ── (temporary; see docs/DEMO_DATA_FEATURE.md to remove) ──
  const fetchDemoStatus = useCallback(async () => {
    if (!activeSiteId) { setDemoStatus({ running: false }); return; }
    try {
      const res = await axios.get(`${API}/oim/demo/status?site_id=${activeSiteId}`);
      setDemoStatus(res.data?.data || { running: false });
    } catch {
      setDemoStatus({ running: false });
    }
  }, [activeSiteId]);

  useEffect(() => { fetchDemoStatus(); }, [fetchDemoStatus]);
  // Poll while running so tick counter stays live
  useEffect(() => {
    if (!demoStatus?.running) return undefined;
    const t = setInterval(fetchDemoStatus, 5000);
    return () => clearInterval(t);
  }, [demoStatus?.running, fetchDemoStatus]);

  const handleDemoToggle = async () => {
    if (!activeSiteId) { showError('Select a site first'); return; }
    setDemoBusy(true);
    try {
      if (demoStatus?.running) {
        await axios.post(`${API}/oim/demo/stop?site_id=${activeSiteId}`);
        showSuccess('Demo generator stopped');
      } else {
        const days = (demoBackfillHours / 24).toFixed(0);
        if (demoBackfillHours >= 168) {
          showSuccess(`Demo generator starting — backfilling ${days} days of history (this may take a moment)…`);
        }
        await axios.post(`${API}/oim/demo/start`, {
          site_id: activeSiteId,
          interval_seconds: 60,
          backfill_hours: demoBackfillHours,
        });
        showSuccess(`Demo generator started — ${days}d backfill + one simulated hour per minute`);
      }
      await fetchDemoStatus();
    } catch (err) {
      console.error('Demo generator toggle failed', err);
      showError(err.response?.data?.detail || 'Demo generator action failed');
    } finally {
      setDemoBusy(false);
    }
  };
  // ── END DEMO ──

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post(`${API}/oim/alerts/${alertId}/acknowledge`);
      showSuccess('Alert acknowledged');
      await fetchAlerts();
    } catch (err) {
      console.error('Acknowledge failed', err);
      showError('Failed to acknowledge alert');
    }
  };

  const status = dashboard?.status || 'EMPTY';
  const kpis = dashboard?.kpis || {};
  const series = dashboard?.series || [];
  const currency = kpis.currency || 'INR';

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3">
                <Activity className="w-7 h-7 text-blue-600" />
                <h1 className="text-3xl font-bold text-gray-900">Online Intelligence</h1>
                <StatusIndicator status={status} />
              </div>
              <p className="text-gray-600 mt-2">
                Hourly Energy Intensity & Cost Intensity — provisional in real time, reconciled at end-of-day.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                {PERIOD_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => { fetchDashboard(); fetchAlerts(); }}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                title="Refresh dashboard"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={handleCompute}
                disabled={computing || !activeSiteId}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium"
                title="Run hourly compute + alert detection now"
              >
                <PlayCircle className="w-4 h-4" />
                {computing ? 'Computing…' : 'Run Compute'}
              </button>
              {/* ── BEGIN DEMO ── (temporary; see docs/DEMO_DATA_FEATURE.md to remove) ── */}
              {/* Backfill window selector — only meaningful before Start. While
                  the demo is running we just show the button. */}
              {!demoStatus?.running && (
                <select
                  value={demoBackfillHours}
                  onChange={(e) => setDemoBackfillHours(Number(e.target.value))}
                  disabled={demoBusy || !activeSiteId}
                  className="px-2 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-700 disabled:bg-gray-100"
                  title="How many hours of past data to backfill when starting the demo. Longer windows take longer to seed but populate the executive-dashboard baseline."
                >
                  <option value={0}>No backfill</option>
                  <option value={24}>24h backfill</option>
                  <option value={168}>7d backfill</option>
                  <option value={720}>30d backfill</option>
                  <option value={1440}>60d backfill</option>
                  <option value={2160}>90d backfill</option>
                </select>
              )}
              <button
                type="button"
                onClick={handleDemoToggle}
                disabled={demoBusy || !activeSiteId}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white disabled:bg-gray-300 ${
                  demoStatus?.running
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
                title={demoStatus?.running
                  ? `Demo generator running · ${demoStatus.ticks_completed || 0}/${demoStatus.max_ticks || 240} ticks · ${demoStatus.rows_inserted || 0} rows so far`
                  : `Start a sandbox data stream (one simulated hour per minute, plus ${demoBackfillHours}h of backfill history) for demos / QA`}
              >
                <Activity className={`w-4 h-4 ${demoStatus?.running ? 'animate-pulse' : ''}`} />
                {demoBusy
                  ? (demoStatus?.running ? 'Stopping…' : 'Starting…')
                  : (demoStatus?.running
                      ? `Stop Demo (${demoStatus.ticks_completed || 0})`
                      : 'Start Demo Data')}
              </button>
              {/* ── END DEMO ── */}
            </div>
          </div>
        </div>

        {/* Sub-tab navigation: Operational | Executive | Steam | Electricity | Water | SEU */}
        <div className="border-b border-gray-200 bg-white rounded-t-lg overflow-x-auto mb-4">
          <nav className="-mb-px flex gap-1 px-2" aria-label="OIM sub-modules">
            {SUB_TABS.map(({ id, label, num, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveView(id)}
                className={`whitespace-nowrap py-3 px-3 inline-flex items-center gap-2 border-b-2 text-sm font-medium transition-colors ${
                  activeView === id
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-mono text-[10px] opacity-70">{num}</span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {!activeSiteId && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
            Select a site to view OIM data.
          </div>
        )}

        {/* M1 Executive Dashboard */}
        {activeView === 'executive'   && <OimExecutivePanel    siteId={activeSiteId} />}
        {/* M2-M5 utility + analytics dashboards */}
        {activeView === 'steam'       && <OimSteamPanel        siteId={activeSiteId} />}
        {activeView === 'electricity' && <OimElectricityPanel  siteId={activeSiteId} />}
        {activeView === 'water'       && <OimWaterPanel        siteId={activeSiteId} />}
        {activeView === 'seu'         && <OimSEUAnalyticsPanel siteId={activeSiteId} />}

        {/* M0 Operational view — original UI starts here, only rendered on the operational tab */}
        {activeView === 'operational' && (<>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title="Cost Intensity"
            value={kpis.cost_intensity_avg}
            unit={`${currency}/kL`}
            trend={kpis.cost_intensity_avg_trend}
            trendIsGood="down"
            trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
            status={status}
            subtle="over period"
            accent="teal"
          />
          <KpiCard
            title="Energy Intensity"
            value={kpis.energy_intensity_avg}
            unit="kWh/kL"
            trend={kpis.energy_intensity_avg_trend}
            trendIsGood="down"
            trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
            status={status}
            subtle="over period"
            accent="blue"
          />
          <KpiCard
            title="Production (total)"
            value={kpis.production_total}
            unit="kL"
            trend={kpis.production_total_trend}
            trendIsGood="up"
            trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
            status={status}
            subtle="sum over period"
            accent="indigo"
          />
          <KpiCard
            title="Energy (total)"
            value={kpis.energy_total}
            unit="kWh"
            trend={kpis.energy_total_trend}
            trendIsGood="down"
            trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
            status={status}
            subtle="sum over period"
            accent="amber"
          />
        </div>

        {/* Resource KPIs (water + waste) — only render if data exists */}
        {(kpis.water_total != null || kpis.waste_total != null) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard
              title="Water Intensity"
              value={kpis.water_intensity_avg}
              unit="kL/kL"
              trend={kpis.water_intensity_avg_trend}
              trendIsGood="down"
              trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
              status={status}
              subtle="over period"
              accent="blue"
            />
            <KpiCard
              title="Water (total)"
              value={kpis.water_total}
              unit="kL"
              trend={kpis.water_total_trend}
              trendIsGood="down"
              trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
              status={status}
              subtle="sum over period"
              accent="indigo"
            />
            <KpiCard
              title="Waste Intensity"
              value={kpis.waste_intensity_avg}
              unit={`${kpis.waste_unit || 'kL'}/kL`}
              trend={kpis.waste_intensity_avg_trend}
              trendIsGood="down"
              trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
              status={status}
              subtle="over period"
              accent="amber"
            />
            <KpiCard
              title="Waste (total)"
              value={kpis.waste_total}
              unit={kpis.waste_unit || 'kL'}
              trend={kpis.waste_total_trend}
              trendIsGood="down"
              trendLabel={`vs previous ${PERIOD_OPTIONS.find(p => p.id === period)?.label?.replace('Last ', '') || period}`}
              status={status}
              subtle="sum over period"
              accent="teal"
            />
          </div>
        )}

        {/* Trend charts — Energy + Cost intensity side by side */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Energy Intensity — Hourly Trend</h2>
              <span className="text-xs text-gray-500">
                {formatRangeShort(dashboard?.range_start, dashboard?.range_end)}
              </span>
            </div>
            <IntensityChart type="energy" data={series} currency={currency} period={period} />
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Cost Intensity — Hourly Trend</h2>
              <span className="text-xs text-gray-500">{currency} per kL</span>
            </div>
            <IntensityChart type="cost" data={series} currency={currency} period={period} />
          </div>
        </div>

        {/* Heatmap — date × hour-of-day efficiency */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Hour-vs-Date Efficiency Heatmap</h2>
            <span className="text-xs text-gray-500">
              Greener = lower energy intensity (more efficient). Best on 7d / 30d periods.
            </span>
          </div>
          <Heatmap data={series} metric="energy_intensity" label="Energy Intensity (kWh/kL)" />
        </div>

        {/* Daily summary + reconcile */}
        <div className="mb-6">
          <DailySummaryPanel
            siteId={activeSiteId}
            currency={currency}
            onReconciled={() => { fetchDashboard(); fetchAlerts(); }}
          />
        </div>

        {/* Alerts panel */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
              {dashboard?.alerts_count?.open > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                  {dashboard.alerts_count.open} open
                </span>
              )}
            </div>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-gray-500 py-4 text-center">No alerts in the last 7 days.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {alerts
                .slice((alertPage - 1) * ALERT_PAGE_SIZE, alertPage * ALERT_PAGE_SIZE)
                .map((a) => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={
                        `px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ` +
                        (a.severity === 'HIGH'   ? 'bg-red-100 text-red-800' :
                         a.severity === 'MEDIUM' ? 'bg-amber-100 text-amber-800' :
                                                   'bg-yellow-100 text-yellow-800')
                      }>{a.severity}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-700">
                        {a.alert_type}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(a.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-gray-800 break-words">{a.message}</div>
                  </div>
                  {a.is_acknowledged ? (
                    <span className="text-xs text-green-700 whitespace-nowrap">acknowledged</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleAcknowledge(a.id)}
                      className="px-2.5 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 rounded border border-gray-300 whitespace-nowrap"
                    >
                      Acknowledge
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {alerts.length > ALERT_PAGE_SIZE && (() => {
            const totalPages = Math.ceil(alerts.length / ALERT_PAGE_SIZE);
            const start = (alertPage - 1) * ALERT_PAGE_SIZE + 1;
            const end = Math.min(alertPage * ALERT_PAGE_SIZE, alerts.length);
            return (
              <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  Showing <span className="font-medium text-gray-700">{start}–{end}</span> of{' '}
                  <span className="font-medium text-gray-700">{alerts.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setAlertPage(p => Math.max(1, p - 1))}
                    disabled={alertPage === 1}
                    className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-700 px-2">
                    Page {alertPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAlertPage(p => Math.min(totalPages, p + 1))}
                    disabled={alertPage >= totalPages}
                    className="p-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Ingestion uploaders */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Data Ingestion</h2>
          <p className="text-xs text-gray-600 mb-4">
            Upload CSVs to populate OIM. Each row carries its own <code className="text-blue-700">site_id</code>.
            After upload, click <strong>Run Compute</strong> above to materialise hourly intensities and run alert detection.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <OimUpload fileType="energy"     siteId={activeSiteId} onUploaded={fetchDashboard} />
            <OimUpload fileType="production" siteId={activeSiteId} onUploaded={fetchDashboard} />
            <OimUpload fileType="cost"       siteId={activeSiteId} onUploaded={fetchDashboard} />
            <OimUpload fileType="water"      siteId={activeSiteId} onUploaded={fetchDashboard} />
            <OimUpload fileType="waste"      siteId={activeSiteId} onUploaded={fetchDashboard} />
          </div>
        </div>

        </>)}{/* end M0 Operational view */}
      </div>
    </div>
  );
};

export default OnlineIntelligenceHub;
